import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { supabase } from './lib/supabase'
import { useAuth } from './contexts/AuthContext'
import { useMembership } from './hooks/useMembership'
import { currencies, CURRENCY_STORAGE_KEY } from './components/CurrencySelector'
import logo from '../traderhub24-logo.svg'
import Footer from './components/Footer'

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'tr', name: 'Türkçe' },
  { code: 'sv', name: 'Svenska' },
] as const

interface AccountProps {
  onBack: () => void
  embedded?: boolean
}

function membershipLabel(membershipType: string) {
  if (membershipType === 'pro') return 'Pro'
  if (membershipType === 'publisher') return 'Publisher'
  return 'Free'
}

export default function Account({ onBack, embedded }: AccountProps) {
  const { t, i18n } = useTranslation('pages')
  const navigate = useNavigate()
  const { user, session, userProfile, profileLoading, membershipType, refreshProfile, signOut } = useAuth()
  const { isPublisher } = useMembership()

  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  const [displayName, setDisplayName] = useState('')
  const [profileSaveLoading, setProfileSaveLoading] = useState(false)
  const [profileSaveError, setProfileSaveError] = useState<string | null>(null)
  const [profileSaveSuccess, setProfileSaveSuccess] = useState<string | null>(null)

  // Publisher profile fields
  const [biography, setBiography] = useState('')
  const [xProfile, setXProfile] = useState('')
  const [instagramProfile, setInstagramProfile] = useState('')
  const [telegramProfile, setTelegramProfile] = useState('')
  const [youtubeProfile, setYoutubeProfile] = useState('')

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmNewPassword, setConfirmNewPassword] = useState('')
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null)

  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const [subscriptionError, setSubscriptionError] = useState<string | null>(null)
  const [billingLoading, setBillingLoading] = useState(false)
  const [checkoutLoadingPlan, setCheckoutLoadingPlan] = useState<'pro' | 'publisher' | null>(null)

  type TabId = 'profile' | 'settings' | 'billing'
  const [activeTab, setActiveTab] = useState<TabId>('profile')

  const [preferredLanguage, setPreferredLanguage] = useState<string>('en')
  const [preferredCurrency, setPreferredCurrency] = useState<string>('USD')
  const [settingsSaveLoading, setSettingsSaveLoading] = useState(false)

  const email = user?.email ?? ''

  useEffect(() => {
    const nextName = userProfile?.display_name || ''
    setDisplayName(nextName)
  }, [userProfile?.display_name])

  useEffect(() => {
    if (isPublisher && userProfile) {
      setBiography(userProfile.biography || '')
      setXProfile(userProfile.x_profile || '')
      setInstagramProfile(userProfile.instagram_profile || '')
      setTelegramProfile(userProfile.telegram_profile || '')
      setYoutubeProfile(userProfile.youtube_profile || '')
    }
  }, [isPublisher, userProfile])

  useEffect(() => {
    if (userProfile) {
      if (userProfile.preferred_language) setPreferredLanguage(userProfile.preferred_language)
      if (userProfile.preferred_currency) setPreferredCurrency(userProfile.preferred_currency)
    }
  }, [userProfile?.preferred_language, userProfile?.preferred_currency])

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false)
      }
    }

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showUserMenu])


  const accountReturnUrl = (() => {
    if (typeof window === 'undefined') return undefined
    return `${window.location.origin}/?page=account`
  })()

  const openBillingPortal = async () => {
    if (!accountReturnUrl) return
    if (!user) return
    if (!session?.access_token) return

    setBillingLoading(true)
    try {
      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        body: { returnUrl: accountReturnUrl },
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (error) throw error
      if (!data?.url) throw new Error('Missing portal URL')
      window.location.assign(data.url)
    } catch (e: any) {
      setSubscriptionError(e?.message || 'Failed to open billing portal')
    } finally {
      setBillingLoading(false)
    }
  }

  const startCheckout = async (planId: 'pro' | 'publisher') => {
    if (!accountReturnUrl) return
    if (!user) return
    if (!session?.access_token) return

    setCheckoutLoadingPlan(planId)
    setSubscriptionError(null)
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { returnUrl: accountReturnUrl, interval: 'month', planId },
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (error) throw error
      if (!data?.url) throw new Error('Missing checkout URL')
      window.location.assign(data.url)
    } catch (e: any) {
      // Supabase FunctionsHttpError often contains the Response in `context`.
      try {
        const ctx = e?.context
        if (ctx && typeof ctx.text === 'function') {
          const bodyText = await ctx.text()
          setSubscriptionError(bodyText || e?.message || 'Failed to start checkout')
        } else {
          setSubscriptionError(e?.message || 'Failed to start checkout')
        }
      } catch {
        setSubscriptionError(e?.message || 'Failed to start checkout')
      }
      setCheckoutLoadingPlan(null)
    }
  }


  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    setProfileSaveError(null)
    setProfileSaveSuccess(null)

    if (!user) {
      setProfileSaveError('Please sign in.')
      return
    }

    setProfileSaveLoading(true)
    try {
      const trimmed = displayName.trim()
      const updateData: any = { display_name: trimmed.length ? trimmed : null }

      // Add publisher fields if user is publisher
      if (isPublisher) {
        updateData.biography = biography.trim() || null
        updateData.x_profile = xProfile.trim() || null
        updateData.instagram_profile = instagramProfile.trim() || null
        updateData.telegram_profile = telegramProfile.trim() || null
        updateData.youtube_profile = youtubeProfile.trim() || null
      }

      const { error } = await supabase
        .from('user_profiles')
        .update(updateData)
        .eq('id', user.id)

      if (error) {
        setProfileSaveError(error.message)
        return
      }

      await refreshProfile()
      setProfileSaveSuccess('Saved.')
    } catch (e: any) {
      setProfileSaveError(e?.message || 'An unexpected error occurred.')
    } finally {
      setProfileSaveLoading(false)
    }
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setPasswordError(null)
    setPasswordSuccess(null)

    if (!user) {
      setPasswordError('Please sign in.')
      return
    }

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters.')
      return
    }

    if (newPassword !== confirmNewPassword) {
      setPasswordError('New passwords do not match.')
      return
    }

    setPasswordLoading(true)
    try {
      // Optional re-auth: improves reliability for some auth setups.
      if (email && currentPassword) {
        const { error: reauthError } = await supabase.auth.signInWithPassword({
          email,
          password: currentPassword,
        })
        if (reauthError) {
          setPasswordError(`Current password could not be verified: ${reauthError.message}`)
          return
        }
      }

      const { error } = await supabase.auth.updateUser({ password: newPassword })
      if (error) {
        setPasswordError(error.message)
        return
      }

      setCurrentPassword('')
      setNewPassword('')
      setConfirmNewPassword('')
      setPasswordSuccess('Your password has been updated.')
    } catch (e: any) {
      setPasswordError(e?.message || 'An unexpected error occurred.')
    } finally {
      setPasswordLoading(false)
    }
  }

  const handleDeleteAccount = async () => {
    setDeleteError(null)

    if (!user) {
      setDeleteError('Please sign in.')
      return
    }

    if (deleteConfirmText.trim().toUpperCase() !== 'DELETE') {
      setDeleteError('To continue, type "DELETE" in the box.')
      return
    }

    setDeleteLoading(true)
    try {
      const { error } = await supabase.rpc('delete_own_account')
      if (error) {
        setDeleteError(error.message)
        return
      }

      await signOut()
      window.location.reload()
    } catch (e: any) {
      setDeleteError(e?.message || 'An unexpected error occurred.')
    } finally {
      setDeleteLoading(false)
    }
  }

  const savePreferredLanguage = async (code: string) => {
    if (!user) return
    setPreferredLanguage(code)
    setSettingsSaveLoading(true)
    try {
      await supabase.from('user_profiles').update({ preferred_language: code }).eq('id', user.id)
      await refreshProfile()
      i18n.changeLanguage(code)
    } finally {
      setSettingsSaveLoading(false)
    }
  }
  const savePreferredCurrency = async (code: string) => {
    if (!user) return
    setPreferredCurrency(code)
    setSettingsSaveLoading(true)
    try {
      await supabase.from('user_profiles').update({ preferred_currency: code }).eq('id', user.id)
      await refreshProfile()
      localStorage.setItem(CURRENCY_STORAGE_KEY, code)
      window.dispatchEvent(new Event('currencyChanged'))
    } finally {
      setSettingsSaveLoading(false)
    }
  }

  return (
    <div className={embedded ? "dashboard-page-content" : "min-h-screen bg-app-bg text-gray-900"}>
      {!embedded && (
      <header className="w-full py-3 px-4 md:px-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <img
            src={logo}
            alt="TraderHub24"
            className="h-12 md:h-20 w-auto cursor-pointer hover:opacity-80 transition-opacity"
            onClick={onBack}
          />
          <div className="flex items-center gap-2">
            {user ? (
              <div className="relative flex items-center gap-2">
                {/* Mobile Menu Button */}
                <button
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className="md:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-lg border border-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {showMobileMenu ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                    )}
                  </svg>
                </button>

                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="px-3 md:px-4 py-2 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2 border border-gray-300"
                    title="Account"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className="hidden md:inline">Account</span>
                  </button>
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-64 bg-app-box rounded-lg shadow-lg border border-gray-200 z-50">
                      <div className="p-4 border-b border-gray-200">
                        <p className="text-sm font-semibold text-gray-900">Account</p>
                        <p className="text-sm text-gray-600 mt-1 truncate">{user.email}</p>
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-xs text-gray-500">Membership</span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                              membershipType === 'pro' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {profileLoading ? 'Loading...' : membershipType.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="p-2">
                        <button
                          onClick={() => {
                            signOut()
                            setShowUserMenu(false)
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* Mobile Sidebar/Menu */}
        {showMobileMenu && user && (
          <div className="md:hidden fixed inset-0 z-[100] flex">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowMobileMenu(false)} />
            <div className="relative w-64 max-w-[80%] h-full bg-app-box shadow-xl flex flex-col p-4 animate-in slide-in-from-left duration-300">
              <div className="flex items-center justify-between mb-8">
                <img src={logo} alt="TraderHub24" className="h-8 w-auto" />
                <button onClick={() => setShowMobileMenu(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <nav className="space-y-2">
                {[
                  { id: "all-trades", label: "All Trades", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" },
                  { id: "analytics", label: "Analytics", icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" },
                  { id: "tools", label: "Tools", icon: "M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a1 1 0 01-1-1v-3a1 1 0 011-1h1a2 2 0 100-4H4a1 1 0 01-1-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" },
                  { id: "strategies", label: "Strategies", icon: "M13 10V3L4 14h7v7l9-11h-7z" },
                  { id: "social-trade", label: "Social Trade", icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" },
                  { id: "community", label: "Community", icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" },
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      // Need to pass to onBack or handle it via a new prop if we wanted 
                      // to navigate from here. For now, Account usually goes back.
                      // If we want to allow navigation, we'd need to change Account's props.
                      // Given current structure, Account is usually reached via onBack.
                      // Let's assume onBack handles returning to App.tsx which handles pages.
                      // But Account is a page itself.
                      onBack();
                      // This is a bit of a workaround since Account doesn't have setCurrentPage.
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors text-gray-700 hover:bg-gray-100`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                    </svg>
                    {item.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        )}
      </header>
      )}

      {(() => {
        const W = embedded ? "div" : "main";
        return (
        <W className={embedded ? "w-full px-4 md:px-6 py-6 space-y-6" : "max-w-6xl mx-auto px-4 md:px-6 py-6 space-y-6"}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">{t('account.title')}</h2>
            <p className="text-sm text-gray-600 mt-1">{t('account.subtitle')}</p>
          </div>
          <button
            onClick={onBack}
            className="w-full md:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors text-center"
          >
            {t('account.back')}
          </button>
        </div>

        {!user ? (
          <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-6">
            <p className="text-sm text-gray-700">You must be signed in to view this page.</p>
          </div>
        ) : (
          <>
            {embedded && (
              <div className="flex border-b border-gray-200 mb-6 gap-0">
                {(['profile', 'settings', 'billing'] as const).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                      activeTab === tab
                        ? 'border-orange-500 text-orange-600'
                        : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                    }`}
                  >
                    {tab === 'profile' && t('account.tabMyProfile')}
                    {tab === 'settings' && t('account.tabSettings')}
                    {tab === 'billing' && t('account.tabBilling')}
                  </button>
                ))}
              </div>
            )}

            {(!embedded || activeTab === 'profile') && (
            <>
            <section className={embedded ? "space-y-4" : "bg-app-box shadow-sm border border-gray-200 rounded-2xl p-6"}>
              {!embedded && <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('account.profile')}</h3>}

              <div className={embedded ? "space-y-4 max-w-md" : "grid grid-cols-1 md:grid-cols-2 gap-6"}>
                <div className="text-sm">
                  <div className={embedded ? "text-xs text-gray-500" : "text-gray-500"}>{t('account.email')}</div>
                  <div className="text-gray-900 font-medium break-all">{email || '-'}</div>
                </div>

                <form onSubmit={handleSaveProfile} className={embedded ? "space-y-3" : "space-y-3"}>
                  <div>
                    <label className={`block font-medium text-gray-700 ${embedded ? "text-xs mb-1" : "text-sm mb-2"}`}>{t('account.name')}</label>
                    <input
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      className={`w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${embedded ? "px-3 py-2 text-sm" : "px-4 py-3"}`}
                      placeholder={t('account.namePlaceholder')}
                    />
                    {!embedded && <p className="text-xs text-gray-500 mt-2">This name is stored in your profile.</p>}
                  </div>

                  {isPublisher && (
                    <>
                      <div>
                        <label className={`block font-medium text-gray-700 ${embedded ? "text-xs mb-1" : "text-sm mb-2"}`}>{t('account.biography')}</label>
                        <textarea
                          value={biography}
                          onChange={(e) => setBiography(e.target.value)}
                          rows={embedded ? 2 : 4}
                          className={`w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${embedded ? "px-3 py-2 text-sm" : "px-4 py-3"}`}
                          placeholder={t('account.biographyPlaceholder')}
                        />
                        {!embedded && <p className="text-xs text-gray-500 mt-2">{t('account.biographyHint')}</p>}
                      </div>

                      <div className={embedded ? "grid grid-cols-2 gap-2" : "grid grid-cols-1 md:grid-cols-2 gap-4"}>
                        {[
                          { key: 'xProfile', value: xProfile, set: setXProfile, placeholder: t('account.xProfilePlaceholder') },
                          { key: 'instagramProfile', value: instagramProfile, set: setInstagramProfile, placeholder: t('account.instagramProfilePlaceholder') },
                          { key: 'telegramProfile', value: telegramProfile, set: setTelegramProfile, placeholder: t('account.telegramProfilePlaceholder') },
                          { key: 'youtubeProfile', value: youtubeProfile, set: setYoutubeProfile, placeholder: t('account.youtubeProfilePlaceholder') },
                        ].map(({ key, value, set, placeholder }) => (
                          <div key={key}>
                            <label className={`block font-medium text-gray-700 ${embedded ? "text-xs mb-1" : "text-sm mb-2"}`}>{t(`account.${key}`)}</label>
                            <input
                              type="text"
                              value={value}
                              onChange={(e) => set(e.target.value)}
                              className={`w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${embedded ? "px-3 py-2 text-sm" : "px-4 py-3"}`}
                              placeholder={placeholder}
                            />
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  <div className="flex items-center gap-3">
                    <button
                      type="submit"
                      disabled={profileSaveLoading}
                      className={`text-white bg-orange-500 border border-orange-500 rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${embedded ? "px-4 py-1.5 text-sm font-medium" : "px-5 py-2 text-sm font-medium"}`}
                    >
                      {profileSaveLoading ? t('account.saving') : t('account.save')}
                    </button>
                    {profileSaveSuccess && <span className="text-xs text-green-600">{profileSaveSuccess}</span>}
                  </div>

                  {profileSaveError && (
                    <div className={`text-red-700 text-sm ${embedded ? "bg-red-50/80 px-3 py-2 rounded-md" : "bg-red-50 border border-red-200 px-4 py-3 rounded-lg"}`}>
                      {profileSaveError}
                    </div>
                  )}
                </form>
              </div>
            </section>
            </>
            )}

            {(!embedded || activeTab === 'billing') && (
            <>
            <section className={embedded ? "space-y-4" : "bg-app-box shadow-sm border border-gray-200 rounded-2xl p-6"}>
              {!embedded && <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('account.subscription')}</h3>}

              <div className={embedded ? "space-y-3" : "mb-6"}>
                <div className="text-sm">
                  <div className={embedded ? "text-xs text-gray-500" : "text-gray-500"}>{t('account.plan')}</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-gray-900 font-semibold">{membershipLabel(membershipType)}</span>
                    {(membershipType === 'pro' || membershipType === 'publisher') && (
                      <button
                        type="button"
                        onClick={openBillingPortal}
                        className="text-sm text-orange-500 hover:text-orange-600 hover:underline"
                      >
                        {billingLoading ? 'Opening…' : t('account.manageBilling')}
                      </button>
                    )}
                  </div>
                  {subscriptionError && (
                    <div className="mt-1 text-xs text-red-700">{subscriptionError}</div>
                  )}
                </div>
              </div>

              {membershipType === 'free' && (
                <div className={embedded ? "flex flex-wrap gap-2" : "mt-6 grid grid-cols-1 md:grid-cols-2 gap-4"}>
                  <button
                    type="button"
                    onClick={() => startCheckout('pro')}
                    disabled={!!checkoutLoadingPlan}
                    className={embedded ? "text-xs font-medium text-orange-600 hover:text-orange-700 border border-orange-200 rounded-md px-3 py-1.5 hover:bg-orange-50" : "block w-full text-left bg-white border border-gray-200 rounded-lg p-4 hover:border-orange-200"}
                  >
                    {embedded ? (checkoutLoadingPlan === 'pro' ? '…' : 'Pro') : (
                      <>
                        <h4 className="text-base font-semibold text-gray-900">Pro</h4>
                        <p className="text-xs text-gray-600 mt-1">For serious traders</p>
                        <span className="text-sm text-orange-500 hover:underline mt-2 inline-block">{checkoutLoadingPlan === 'pro' ? 'Redirecting…' : t('account.upgrade')}</span>
                      </>
                    )}
                  </button>
                  {!embedded && (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="text-base font-semibold text-gray-900">Publisher</h4>
                        <p className="text-xs text-gray-600 mt-1">For content publishers</p>
                      </div>
                    </div>
                    <a
                      href="#"
                      onClick={(e) => { e.preventDefault(); startCheckout('publisher') }}
                      className="text-sm text-orange-500 hover:text-orange-600 hover:underline"
                    >
                      {checkoutLoadingPlan === 'publisher' ? 'Redirecting…' : 'Upgrade to Publisher'}
                    </a>
                  </div>
                  )}
                  {embedded && (
                  <button
                    type="button"
                    onClick={() => startCheckout('publisher')}
                    disabled={!!checkoutLoadingPlan}
                    className="text-xs font-medium text-orange-600 hover:text-orange-700 border border-orange-200 rounded-md px-3 py-1.5 hover:bg-orange-50"
                  >
                    {checkoutLoadingPlan === 'publisher' ? '…' : 'Publisher'}
                  </button>
                  )}
                </div>
              )}

              {membershipType === 'pro' && (
                <div className={embedded ? "mt-1" : "mt-6"}>
                  <button
                    type="button"
                    onClick={openBillingPortal}
                    className={embedded ? "text-xs text-orange-600 hover:underline" : "block w-full text-left bg-white border border-gray-200 rounded-lg p-4"}
                  >
                    {embedded ? (billingLoading ? 'Opening…' : 'Switch to Publisher') : (
                      <>
                        <h4 className="text-base font-semibold text-gray-900">Publisher</h4>
                        <p className="text-xs text-gray-600 mt-1">For content publishers</p>
                        <span className="text-sm text-orange-500 hover:underline mt-2 inline-block">{billingLoading ? 'Opening…' : 'Switch to Publisher'}</span>
                      </>
                    )}
                  </button>
                </div>
              )}

              {membershipType === 'publisher' && (
                <div className={embedded ? "mt-1" : "mt-6"}>
                  <button
                    type="button"
                    onClick={openBillingPortal}
                    className={embedded ? "text-xs text-orange-600 hover:underline" : "block w-full text-left bg-white border border-gray-200 rounded-lg p-4"}
                  >
                    {embedded ? (billingLoading ? 'Opening…' : 'Downgrade to Pro') : (
                      <>
                        <h4 className="text-base font-semibold text-gray-900">Pro</h4>
                        <p className="text-xs text-gray-600 mt-1">For serious traders</p>
                        <span className="text-sm text-orange-500 hover:underline mt-2 inline-block">{billingLoading ? 'Opening…' : 'Downgrade to Pro'}</span>
                      </>
                    )}
                  </button>
                </div>
              )}

            </section>
            </>
            )}

            {(!embedded || activeTab === 'settings') && (
            <>
            {/* Language & Currency cards - design from reference */}
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <div className="flex items-center justify-between gap-4">
                  <h3 className="text-sm font-semibold text-gray-900">{t('account.settingsLanguage')}</h3>
                  <select
                    value={preferredLanguage}
                    onChange={(e) => savePreferredLanguage(e.target.value)}
                    disabled={settingsSaveLoading}
                    className="flex-1 max-w-[200px] ml-auto px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    {LANGUAGES.map((lang) => (
                      <option key={lang.code} value={lang.code}>{lang.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <div className="flex items-center justify-between gap-4">
                  <h3 className="text-sm font-semibold text-gray-900">{t('account.settingsCurrency')}</h3>
                  <select
                    value={preferredCurrency}
                    onChange={(e) => savePreferredCurrency(e.target.value)}
                    disabled={settingsSaveLoading}
                    className="flex-1 max-w-[200px] ml-auto px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    {currencies.map((c) => (
                      <option key={c.code} value={c.code}>{c.name} ({c.code})</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Danger area - red border */}
            <div className="border-2 border-red-200 rounded-xl bg-red-50/30 p-4 md:p-6 mt-6 space-y-6">
              <h3 className="text-base font-semibold text-red-700">{t('account.dangerZone')}</h3>

              <section>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">{t('account.changePassword')}</h4>
                <form onSubmit={handleChangePassword} className={embedded ? "space-y-3 max-w-sm" : "space-y-4 max-w-md"}>
                  <div>
                    <label className={`block font-medium text-gray-700 ${embedded ? "text-xs mb-1" : "text-sm mb-2"}`}>{t('account.currentPassword')}</label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className={`w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${embedded ? "px-3 py-2 text-sm" : "px-4 py-3"}`}
                      placeholder="••••••••"
                      autoComplete="current-password"
                    />
                    {!embedded && <p className="text-xs text-gray-500 mt-2">In some cases, re-authentication may be required for security.</p>}
                  </div>

                  <div>
                    <label className={`block font-medium text-gray-700 ${embedded ? "text-xs mb-1" : "text-sm mb-2"}`}>{t('account.newPassword')}</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={6}
                      className={`w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${embedded ? "px-3 py-2 text-sm" : "px-4 py-3"}`}
                      placeholder="At least 6 characters"
                      autoComplete="new-password"
                    />
                  </div>

                  <div>
                    <label className={`block font-medium text-gray-700 ${embedded ? "text-xs mb-1" : "text-sm mb-2"}`}>{t('account.confirmNewPassword')}</label>
                    <input
                      type="password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      required
                      minLength={6}
                      className={`w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${embedded ? "px-3 py-2 text-sm" : "px-4 py-3"}`}
                      placeholder="Re-enter your new password"
                      autoComplete="new-password"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={passwordLoading}
                    className={`bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${embedded ? "px-4 py-2 text-sm" : "w-full py-3 px-4"}`}
                  >
                    {passwordLoading ? t('account.updating') : t('account.updatePassword')}
                  </button>

                  {passwordError && (
                    <div className={`text-red-700 text-sm ${embedded ? "bg-red-50/80 px-3 py-2 rounded-md" : "bg-red-50 border border-red-200 px-4 py-3 rounded-lg"}`}>{passwordError}</div>
                  )}
                  {passwordSuccess && (
                    <div className={`text-green-700 text-sm ${embedded ? "px-0" : "bg-green-50 border border-green-200 px-4 py-3 rounded-lg"}`}>{passwordSuccess}</div>
                  )}
                </form>
              </section>

              <section className="pt-4 border-t border-red-200/60">
                <h4 className="text-sm font-semibold text-red-700 mb-1">{t('account.deleteAccount')}</h4>
                <p className={embedded ? "text-xs text-gray-600 mb-3" : "text-sm text-gray-700 mb-4"}>
                  This action cannot be undone. Your data (trades, strategies, etc.) will be permanently deleted.
                </p>

                <div className={embedded ? "space-y-2 max-w-sm" : "max-w-md space-y-3"}>
                  <label className={`block font-medium text-gray-700 ${embedded ? "text-xs mb-1" : "text-sm mb-2"}`}>
                    To continue, type <span className="font-semibold">DELETE</span>
                  </label>
                  <input
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    className={`w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent ${embedded ? "px-3 py-2 text-sm" : "px-4 py-3"}`}
                    placeholder="DELETE"
                  />
                  <button
                    onClick={handleDeleteAccount}
                    disabled={deleteLoading}
                    className={`bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${embedded ? "px-4 py-2 text-sm" : "w-full py-3 px-4"}`}
                  >
                    {deleteLoading ? 'Deleting...' : 'Permanently delete my account'}
                  </button>
                  {deleteError && (
                    <div className={`text-red-700 text-sm ${embedded ? "bg-red-50/80 px-3 py-2 rounded-md" : "bg-red-50 border border-red-200 px-4 py-3 rounded-lg"}`}>{deleteError}</div>
                  )}
                </div>
              </section>
            </div>
            </>
            )}
          </>
        )}
        </W>
        );
      })()}

      <Footer currentPage={undefined} onNavigate={(page) => {
        if (page === "privacy-policy") navigate("/privacy");
        else if (page === "terms-and-conditions") navigate("/terms");
        else if (page === "risk-warning") navigate("/risk-warning");
        else if (page === "documentation") navigate("/documentation");
        else if (page === "contact") navigate("/contact");
        else if (page === "home") onBack();
        else if (page === "changelog") window.location.href = "/changelog";
      }} />
    </div>
  )
}



